/**
 * variant-picker-visual.js
 *
 * Visual variant picker for Kit Solaire / Kit Autoconsommation products.
 * Uses EVENT DELEGATION so handlers survive DOM replacements by the theme's
 * variant-picker JS (which re-renders section HTML on every variant change).
 *
 * Bug fixes:
 *  - Saves/restores open section + scroll position across DOM replacements
 *  - Updates recap amounts (not just labels) from visual selection prices
 *  - Injects price into CTA button
 */

(function () {
  'use strict';

  // Persistent state that survives DOM replacement
  var vpState = {
    openSection: null,  // which data-vp-section is open
    scrollY: 0
  };

  // =====================================================================
  // HELPERS
  // =====================================================================

  function fmtPrice(cents) {
    return (cents / 100).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + '\u00a0\u20AC';
  }

  // =====================================================================
  // SAVE STATE — called before every sync that triggers DOM replacement
  // =====================================================================

  function saveState(wrapper) {
    if (!wrapper) return;
    var openSection = wrapper.querySelector('.vp-section.vp-open');
    vpState.openSection = openSection ? openSection.dataset.vpSection : null;
    vpState.scrollY = window.scrollY;

    // No visual hack needed — accordion uses display:none (instant)
  }

  // =====================================================================
  // RESTORE STATE — called after DOM replacement (on:variant:change)
  // =====================================================================

  function restoreState(wrapper) {
    if (!wrapper) return;

    // Open section is already restored via on:variant:before-replace-element.
    // We just need to restore scroll position here.
    if (vpState.scrollY != null) {
      window.scrollTo(0, vpState.scrollY);
    }
  }

  // =====================================================================
  // UPDATE RECAP — labels AND amounts from visual selection
  // =====================================================================

  function updateRecap(wrapper, variantPrice) {
    var recap = wrapper.querySelector('[data-vp-recap]');
    if (!recap) return;

    // --- Total ---
    if (variantPrice != null) {
      var totalEl = recap.querySelector('[data-vp-total-price]');
      if (totalEl) totalEl.textContent = fmtPrice(variantPrice);
    }

    // --- Fixation label + amount ---
    var fixCat = wrapper.querySelector('.vp-fixation-cat.vp-selected');
    var fixLabelEl = recap.querySelector('[data-vp-recap-fixation-label]');
    var fixAmountEl = recap.querySelector('[data-vp-recap-fixation-amount]');

    if (fixCat && fixCat.dataset.vpCat === 'sans') {
      if (fixLabelEl) fixLabelEl.textContent = 'Sans fixation';
      if (fixAmountEl) fixAmountEl.textContent = '\u2014';
    } else {
      var subSelected = wrapper.querySelector('.vp-sub-option.vp-selected');
      if (subSelected) {
        var subName = subSelected.querySelector('.vp-sub-name');
        var subPrice = subSelected.querySelector('.vp-sub-price');
        if (fixLabelEl && subName) fixLabelEl.textContent = subName.textContent.trim();
        if (fixAmountEl && subPrice) fixAmountEl.textContent = subPrice.textContent.trim();
      }
    }

    // --- Batterie label + amount ---
    var batCard = wrapper.querySelector('.vp-battery-card.vp-selected');
    var batLabelEl = recap.querySelector('[data-vp-recap-batterie-label]');
    var batAmountEl = recap.querySelector('[data-vp-recap-batterie-amount]');
    if (batCard) {
      if (batLabelEl) batLabelEl.textContent = batCard.dataset.vpBatterieValue;
      var batPrice = batCard.querySelector('.vp-bat-price');
      if (batAmountEl && batPrice) {
        var batText = batPrice.textContent.trim();
        batAmountEl.textContent = batText === 'Inclus' ? '\u2014' : batText;
      }
    }

    // --- Suivi label + amount ---
    var suiviCard = wrapper.querySelector('.vp-suivi-option.vp-selected');
    var suiviLabelEl = recap.querySelector('[data-vp-recap-suivi-label]');
    var suiviAmountEl = recap.querySelector('[data-vp-recap-suivi-amount]');
    if (suiviCard) {
      if (suiviLabelEl) suiviLabelEl.textContent = suiviCard.dataset.vpSuiviValue;
      var suiviPrice = suiviCard.querySelector('.vp-suivi-price');
      if (suiviAmountEl && suiviPrice) {
        var suiviText = suiviPrice.textContent.trim();
        suiviAmountEl.textContent = suiviText === 'Inclus' ? '\u2014' : suiviText;
      }
    }
  }

  // =====================================================================
  // UPDATE CTA BUTTON with price
  // =====================================================================

  function updateCTA(container, variant) {
    if (!variant || !variant.price) return;
    var section = container.closest('.shopify-section') || container.closest('.js-quickbuy');
    if (!section) return;

    var addBtn = section.querySelector('[name="add"]');
    if (!addBtn) return;

    var priceText = fmtPrice(variant.price);

    if (variant.available) {
      var baseText = addBtn.dataset.addToCartText || 'Ajouter au panier';
      var newText = baseText + ' \u2014 ' + priceText;

      // Preserve chevron/SVG inside the button by targeting only the text.
      // 1) If there's a dedicated label span, update only that.
      var labelSpan = addBtn.querySelector('[data-vp-cta-label]');
      if (labelSpan) {
        labelSpan.textContent = newText;
        return;
      }

      // 2) If the button contains any SVG/icon child, update only the first
      //    text node to avoid erasing the chevron.
      var hasSvgChild = !!addBtn.querySelector('svg, img');
      if (hasSvgChild) {
        var firstTextNode = null;
        for (var i = 0; i < addBtn.childNodes.length; i++) {
          var node = addBtn.childNodes[i];
          if (node.nodeType === 3 && node.nodeValue && node.nodeValue.trim()) {
            firstTextNode = node;
            break;
          }
        }
        if (firstTextNode) {
          firstTextNode.nodeValue = newText;
        } else {
          // Fallback: prepend a text node so we don't clobber the SVG
          addBtn.insertBefore(document.createTextNode(newText), addBtn.firstChild);
        }
        return;
      }

      // 3) No SVG — safe to set textContent directly.
      addBtn.textContent = newText;
    }
  }

  // =====================================================================
  // EVENT DELEGATION — all handlers survive DOM replacement
  // =====================================================================

  document.addEventListener('click', function (evt) {
    var target = evt.target;

    // --- Accordion toggle ---
    var toggleHeader = target.closest('[data-vp-toggle]');
    if (toggleHeader) {
      evt.preventDefault();
      var section = toggleHeader.closest('.vp-section');
      var wrapper = toggleHeader.closest('.vp-wrapper');
      if (!section || !wrapper) return;

      var isOpen = section.classList.contains('vp-open');
      wrapper.querySelectorAll('.vp-section').forEach(function (s) {
        s.classList.remove('vp-open');
      });
      if (!isOpen) section.classList.add('vp-open');
      return;
    }

    // --- Fixation category card ---
    var catCard = target.closest('.vp-fixation-cat');
    if (catCard) {
      var wrapper = catCard.closest('.vp-wrapper');
      var container = catCard.closest('variant-picker');
      if (!wrapper || !container) return;

      var cat = catCard.dataset.vpCat;

      // Update category UI
      wrapper.querySelectorAll('.vp-fixation-cat').forEach(function (c) {
        c.classList.remove('vp-selected');
      });
      catCard.classList.add('vp-selected');

      // Has-sub class
      var catsGrid = catCard.closest('.vp-fixation-cats');
      if (catsGrid) {
        catsGrid.classList.toggle('vp-has-sub', cat !== 'sans');
      }

      // Show/hide sub panels
      wrapper.querySelectorAll('.vp-fixation-sub').forEach(function (sub) {
        sub.classList.remove('vp-visible');
      });
      if (cat !== 'sans') {
        var targetSub = wrapper.querySelector('[data-vp-sub="' + cat + '"]');
        if (targetSub) targetSub.classList.add('vp-visible');
      }

      // Show/hide orientation & disposition
      var orientEl = wrapper.querySelector('[data-vp-orientation]');
      var dispoEl = wrapper.querySelector('[data-vp-disposition]');
      if (cat === 'toit') {
        if (orientEl) orientEl.classList.add('vp-visible');
        if (dispoEl) dispoEl.classList.add('vp-visible');
      } else {
        if (orientEl) orientEl.classList.remove('vp-visible');
        if (dispoEl) dispoEl.classList.remove('vp-visible');
      }

      // Direct selection for "Sans fixation"
      if (cat === 'sans' && catCard.dataset.vpFixationValue) {
        wrapper.querySelectorAll('.vp-sub-option').forEach(function (s) {
          s.classList.remove('vp-selected');
        });
        saveState(wrapper);
        syncRadioForOption(container, 'fixation', catCard.dataset.vpFixationValue);
        updateSummary(wrapper, 'fixation', catCard.dataset.vpFixationValue);
      } else if (cat !== 'sans') {
        // Auto-select first sub-option if none selected
        var visibleSub = wrapper.querySelector('[data-vp-sub="' + cat + '"]');
        if (visibleSub && !visibleSub.querySelector('.vp-sub-option.vp-selected')) {
          var firstSub = visibleSub.querySelector('.vp-sub-option');
          if (firstSub) firstSub.click();
        }
      }
      return;
    }

    // --- Fixation sub-option ---
    var subOption = target.closest('.vp-sub-option');
    if (subOption) {
      var wrapper = subOption.closest('.vp-wrapper');
      var container = subOption.closest('variant-picker');
      if (!wrapper || !container) return;

      wrapper.querySelectorAll('.vp-sub-option').forEach(function (s) {
        s.classList.remove('vp-selected');
      });
      subOption.classList.add('vp-selected');

      var fixValue = subOption.dataset.vpFixationValue;
      saveState(wrapper);
      syncRadioForOption(container, 'fixation', fixValue);
      updateSummary(wrapper, 'fixation', fixValue);
      return;
    }

    // --- Battery card ---
    var batteryCard = target.closest('.vp-battery-card');
    if (batteryCard) {
      var wrapper = batteryCard.closest('.vp-wrapper');
      var container = batteryCard.closest('variant-picker');
      if (!wrapper || !container) return;

      wrapper.querySelectorAll('.vp-battery-card').forEach(function (c) {
        c.classList.remove('vp-selected');
      });
      batteryCard.classList.add('vp-selected');

      var batValue = batteryCard.dataset.vpBatterieValue;
      saveState(wrapper);
      syncRadioForOption(container, 'batterie', batValue);
      updateSummary(wrapper, 'batterie', batValue);
      return;
    }

    // --- Suivi option ---
    var suiviOption = target.closest('.vp-suivi-option');
    if (suiviOption) {
      var wrapper = suiviOption.closest('.vp-wrapper');
      var container = suiviOption.closest('variant-picker');
      if (!wrapper || !container) return;

      wrapper.querySelectorAll('.vp-suivi-option').forEach(function (o) {
        o.classList.remove('vp-selected');
      });
      suiviOption.classList.add('vp-selected');

      var suiviValue = suiviOption.dataset.vpSuiviValue;
      saveState(wrapper);
      syncRadioForOption(container, 'suivi', suiviValue);
      updateSummary(wrapper, 'suivi', suiviValue);
      return;
    }

    // --- Orientation toggle ---
    var orientOption = target.closest('.vp-orient-option');
    if (orientOption) {
      var toggle = orientOption.closest('[data-vp-orient-toggle]');
      var container = orientOption.closest('variant-picker');
      if (!toggle) return;

      var orientation = orientOption.dataset.orientation;
      toggle.querySelectorAll('.vp-orient-option').forEach(function (o) {
        o.classList.remove('vp-active');
      });
      orientOption.classList.add('vp-active');
      toggle.classList.toggle('vp-paysage', orientation === 'paysage');

      if (container) {
        var propInput = container.querySelector('[data-vp-property="orientation"]');
        if (propInput) propInput.value = orientation === 'portrait' ? 'Portrait' : 'Paysage';
      }
      return;
    }

    // --- Disposition card ---
    var dispoCard = target.closest('.vp-disposition-card');
    if (dispoCard) {
      var wrapper = dispoCard.closest('.vp-wrapper');
      var container = dispoCard.closest('variant-picker');
      if (!wrapper) return;

      wrapper.querySelectorAll('.vp-disposition-card').forEach(function (c) {
        c.classList.remove('vp-selected');
      });
      dispoCard.classList.add('vp-selected');

      if (container) {
        var propInput = container.querySelector('[data-vp-property="disposition"]');
        if (propInput) {
          propInput.value = dispoCard.dataset.dispoValue
            || (dispoCard.dataset.disposition === '2-lignes' ? '2 lignes' : '1 ligne');
        }
      }
      return;
    }

    // --- Amperage card (coffret tri — line item property) ---
    var ampCard = target.closest('.vp-amperage-card');
    if (ampCard) {
      var wrapper = ampCard.closest('.vp-wrapper');
      var container = ampCard.closest('variant-picker');
      if (!wrapper) return;

      wrapper.querySelectorAll('.vp-amperage-card').forEach(function (c) {
        c.classList.remove('vp-selected');
      });
      ampCard.classList.add('vp-selected');

      var ampValue = ampCard.dataset.vpAmperageValue;
      if (container) {
        var propInput = container.querySelector('[data-vp-property="coffret-tri"]');
        if (propInput) propInput.value = ampValue;
      }

      // Update accordion header summary (e.g., "25 A · 15 kVA")
      var ampBig = ampCard.querySelector('.vp-amp-big');
      var ampKva = ampCard.querySelector('.vp-amp-kva');
      if (ampBig && ampKva) {
        updateSummary(wrapper, 'coffret-tri', ampBig.textContent.trim() + ' · ' + ampKva.textContent.trim());
      }
      return;
    }

  });

  // =====================================================================
  // VALIDATION — block add-to-cart on tri kits if Coffret NF C 15-100 not chosen
  // =====================================================================

  document.addEventListener('click', function (evt) {
    var addBtn = evt.target.closest('[name="add"]');
    if (!addBtn) return;
    // The coffret-tri hidden input is bound to the form via the form="" attribute
    // but lives OUTSIDE the <form> in the DOM — so scope the lookup to the section,
    // not the form subtree.
    var container = addBtn.closest('.shopify-section') || addBtn.closest('.js-quickbuy') || document;
    var coffretInput = container.querySelector('[data-vp-property="coffret-tri"]');
    if (!coffretInput) return; // mono kit (no coffret selector), no validation needed
    if (coffretInput.value && coffretInput.value.trim() !== '') return; // already chosen
    // Block submit
    evt.preventDefault();
    evt.stopImmediatePropagation();
    var section = container.querySelector('[data-vp-section="coffret-tri"]');
    if (section) {
      // Open the accordion
      var wrapper = section.closest('.vp-wrapper');
      if (wrapper) {
        wrapper.querySelectorAll('.vp-section').forEach(function (s) { s.classList.remove('vp-open'); });
      }
      section.classList.add('vp-open');
      section.classList.add('vp-error');
      // Smooth-scroll into view
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Clear error after 4s
      setTimeout(function () { section.classList.remove('vp-error'); }, 4000);
    }
  }, true);

  // =====================================================================
  // INTERCEPT BEFORE-REPLACE — modify the new HTML BEFORE insertion
  // The theme dispatches this event right before replaceWith() is called.
  // We mutate the new element so it already has the correct vp-open class
  // by the time it lands in the DOM. No flash possible.
  // =====================================================================

  document.addEventListener('on:variant:before-replace-element', function (evt) {
    var replaceWith = evt.detail && evt.detail.replaceWith;
    if (!replaceWith || !replaceWith.querySelector) return;

    // Look for our visual picker in the new HTML
    var newWrapper = replaceWith.classList && replaceWith.classList.contains('vp-wrapper')
      ? replaceWith
      : replaceWith.querySelector('.vp-wrapper');

    if (!newWrapper) return;

    // Apply saved open section to new HTML before it's inserted
    if (vpState.openSection) {
      newWrapper.querySelectorAll('.vp-section').forEach(function (s) {
        s.classList.remove('vp-open');
      });
      var toOpen = newWrapper.querySelector('[data-vp-section="' + vpState.openSection + '"]');
      if (toOpen) toOpen.classList.add('vp-open');
    }
  });

  // =====================================================================
  // LISTEN FOR VARIANT CHANGE — update recap + CTA (state already restored)
  // =====================================================================

  document.addEventListener('on:variant:change', function (evt) {
    var container = evt.target.closest('variant-picker');
    if (!container || !container.classList.contains('variant-picker-visual')) return;

    var variant = evt.detail && evt.detail.variant;
    var wrapper = container.querySelector('.vp-wrapper');
    if (!wrapper) return;

    // Fallback restore in case MutationObserver didn't catch it
    if (vpState.openSection) {
      restoreState(wrapper);
    }

    // Update recap with amounts
    var price = variant ? variant.price : null;
    updateRecap(wrapper, price);

    // Update CTA button
    if (variant) updateCTA(container, variant);
  });

  // =====================================================================
  // INITIAL SETUP on page load
  // =====================================================================

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('variant-picker.variant-picker-visual').forEach(function (container) {
      var wrapper = container.querySelector('.vp-wrapper');
      if (!wrapper) return;

      // Initial recap
      var jsonEl = container.querySelector('script[type="application/json"]');
      if (jsonEl) {
        try {
          var variant = JSON.parse(jsonEl.textContent);
          if (variant) {
            updateRecap(wrapper, variant.price);
            updateCTA(container, variant);
          }
        } catch (e) { /* ignore */ }
      }

      // Pre-select the battery that matches the kit power (from
      // product.metafields.specs.batterie_defaut, rendered as data attribute).
      // Only when the visitor hasn't explicitly chosen a variant via the URL,
      // so shared/deep links keep their selection. The display order of the
      // cards stays ascending — we only change which one is selected.
      var defBat = container.dataset.vpDefaultBatterie;
      if (defBat) {
        var hasVariantParam = new URLSearchParams(window.location.search).has('variant');
        if (!hasVariantParam) {
          var targetCard = wrapper.querySelector(
            '.vp-battery-card[data-vp-batterie-value="' + defBat + '"]'
          );
          if (targetCard && !targetCard.classList.contains('vp-selected')) {
            targetCard.click();
          }
        }
      }
    });
  });

  // =====================================================================
  // SYNC HIDDEN RADIO & DISPATCH CHANGE
  // =====================================================================

  function syncRadioForOption(container, optionType, value) {
    var optionNameMap = {
      'fixation': 'Fixation',
      'batterie': 'Batterie',
      'suivi': 'Suivi Conso'
    };

    var possibleNames = [optionNameMap[optionType]];
    if (optionType === 'suivi') {
      possibleNames.push('Suivi');
      possibleNames.push('Suivi conso');
    }
    if (optionType === 'batterie') {
      // The battery option was renamed "Batterie" → "Extensions"; match both
      // so selecting a battery card actually switches the variant (and price).
      possibleNames.push('Extensions');
    }

    var fieldset = null;
    for (var i = 0; i < possibleNames.length; i++) {
      fieldset = container.querySelector(
        '.option-selector[data-option="' + possibleNames[i] + '"]'
      );
      if (fieldset) break;
    }

    if (!fieldset) {
      console.warn('[vp] No fieldset for:', optionType, possibleNames);
      return;
    }

    var radios = fieldset.querySelectorAll('input[type="radio"]');
    var targetRadio = null;
    for (var j = 0; j < radios.length; j++) {
      if (radios[j].value === value) {
        targetRadio = radios[j];
        break;
      }
    }

    if (!targetRadio) {
      console.warn('[vp] No radio for:', value, 'in', optionType);
      return;
    }

    targetRadio.checked = true;
    // Use CustomEvent with noFocus: true to prevent the theme from focusing
    // the radio input (which causes the page to scroll)
    targetRadio.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      detail: { noFocus: true }
    }));
  }

  // =====================================================================
  // UPDATE SUMMARY TEXT in accordion headers
  // =====================================================================

  function updateSummary(wrapper, optionType, value) {
    var summaryEl = wrapper.querySelector('[data-vp-summary="' + optionType + '"]');
    if (summaryEl) summaryEl.textContent = value;
  }

  // =====================================================================
  // CLIC D'OPTION -> FOCUS DU VISUEL CORRESPONDANT (par alt du média)
  // Générique & extensible : pour lier un nouveau choix à un visuel, ajouter
  // une entrée à VP_VISUAL_LINKS (sélecteur de la carte + regex testée sur
  // l'alt du média de la galerie). Le focus ne se déclenche que si un média
  // correspondant existe sur la fiche (sinon no-op : sûr sur toutes les fiches).
  // =====================================================================

  // Fixation : valeur d'option -> regex testée sur l'alt du média (Kit solaire).
  // Tirefonds Bois ET Métal pointent sur le MÊME visuel (/tirefonds/i).
  function vpFixRx(value) {
    var v = (value || '').toLowerCase();
    if (!v || v.indexOf('sans fixation') !== -1) return null;
    if (v.indexOf('trisole') !== -1) return /trisole/i;
    if (v.indexOf('gse') !== -1) return /gse\s*evo/i;
    if (v.indexOf('tirefonds') !== -1) return /tirefonds/i;
    if (v.indexOf('ardoise') !== -1) return /ardoise/i;
    if (v.indexOf('plate') !== -1) return /tuile plate/i;
    if (v.indexOf('canique') !== -1) return /tuile m[eé]canique/i;
    return null;
  }

  var VP_VISUAL_LINKS = [
    // Clic sur une batterie -> visuel batterie (toutes autoconso)
    { match: '.vp-battery-card', alt: /batterie/i },
    // Clic sur "Avec suivi" -> visuel suivi conso ; "Sans suivi" -> retour cover
    // (uniquement si un visuel suivi existe sur la fiche, sinon no-op).
    {
      match: '.vp-suivi-option',
      alt: /suivi/i,
      when: function (el) { return /avec/i.test(el.dataset.vpSuiviValue || ''); },
      elseCover: true
    },
    // Clic sur une fixation -> son visuel dédié (Kit solaire). "Sans fixation" -> cover.
    // Couvre les sous-options ([data-vp-fixation-value]) ET les cartes catégorie sol/toit
    // (qui auto-sélectionnent une sous-option). No-op sur les fiches sans visuels fixation
    // par type (ex. autoconso : coverGuard ne matche pas "Systèmes de fixation disponibles").
    {
      match: '[data-vp-fixation-value], .vp-fixation-cat[data-vp-cat]',
      altFor: function (el) {
        var v = el.dataset.vpFixationValue;
        if (!v && el.dataset.vpCat && el.dataset.vpCat !== 'sans') {
          var w = el.closest('.vp-wrapper');
          var sub = w && (
            w.querySelector('.vp-fixation-sub[data-vp-sub="' + el.dataset.vpCat + '"] .vp-sub-option.vp-selected')
            || w.querySelector('.vp-fixation-sub[data-vp-sub="' + el.dataset.vpCat + '"] .vp-sub-option')
          );
          v = sub && sub.dataset.vpFixationValue;
        }
        return vpFixRx(v);
      },
      coverIfNull: true,
      coverGuard: /fixation (toiture|au sol)/i
    }
    // --- Pour ajouter d'autres liens, dé-commenter / compléter : ---
    // Coffret AC : { match: '.vp-coffret-card, .vp-amperage-card', alt: /coffret/i },
    // Onduleur :   { match: '.vp-onduleur-card', alt: /onduleur|micro-onduleur/i },
  ];

  function vpGallery(container) {
    var section = container.closest('.shopify-section') || container.closest('.js-quickbuy') || document;
    return section.querySelector('media-gallery');
  }
  function vpFindItem(gallery, rx) {
    if (!gallery) return null;
    var items = gallery.querySelectorAll('.media-viewer__item');
    for (var i = 0; i < items.length; i++) {
      var img = items[i].querySelector('img');
      if (img && img.alt && rx.test(img.alt)) return items[i];
    }
    return null;
  }
  function vpFocusItem(gallery, item) {
    if (gallery && item && typeof gallery.customSetActiveMedia === 'function') {
      gallery.customSetActiveMedia(item, true);
    }
  }

  document.addEventListener('click', function (evt) {
    for (var i = 0; i < VP_VISUAL_LINKS.length; i++) {
      var link = VP_VISUAL_LINKS[i];
      var el = evt.target.closest(link.match);
      if (!el) continue;
      var container = el.closest('variant-picker');
      if (!container) return;
      var gallery = vpGallery(container);
      if (!gallery) return;

      // Gate when() (ex. suivi "Avec" vs "Sans")
      if (link.when && !link.when(el)) {
        if (link.elseCover && vpFindItem(gallery, link.alt)) {
          var firstS = gallery.querySelector('.media-viewer__item');
          if (firstS) vpFocusItem(gallery, firstS);
        }
        return;
      }

      // Regex cible : statique (alt) ou dynamique (altFor)
      var rx = link.altFor ? link.altFor(el) : link.alt;
      if (!rx) {
        if (link.coverIfNull && (!link.coverGuard || vpFindItem(gallery, link.coverGuard))) {
          var firstC = gallery.querySelector('.media-viewer__item');
          if (firstC) vpFocusItem(gallery, firstC);
        }
        return;
      }
      var item = vpFindItem(gallery, rx);
      if (item) vpFocusItem(gallery, item);
      return;
    }
  });

  // Test-only hook: expose internals on window when running in Vitest/jsdom
  if (typeof window !== 'undefined' && window.__VP_EXPOSE_INTERNALS__) {
    window.__VP_TEST__ = {
      fmtPrice: fmtPrice,
      saveState: saveState,
      restoreState: restoreState,
      updateRecap: updateRecap,
      updateCTA: updateCTA,
      syncRadioForOption: syncRadioForOption,
      updateSummary: updateSummary,
      getState: function () { return vpState; }
    };
  }

})();